import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { HelpCircle } from 'lucide-react';

import { PunchupToggle } from 'features/common/components/Punchup/primitives/forms/fields/PunchupToggle';
import { PunchupTooltip } from 'features/common/components/Punchup/primitives/feedback/PunchupTooltip';
import styles from './PunchupAutoRefreshToggle.module.css';

export interface PunchupAutoRefreshToggleProps {
	checked: boolean;
	onChange: (next: boolean) => void;
	/** Toggle id/name + the label's `htmlFor`; override only if two instances ever share a page. */
	id?: string;
}

/**
 * The shared opt-in "Auto-refresh" toggle for live monitoring views (Scanned Tickets, the Scan-Tickets
 * attendance strip). It owns the canonical label + help copy so the affordance reads identically wherever it
 * appears; pair it with `useAutoRefresh` for the on/off state and the poll interval. Composes the toggle +
 * tooltip primitives only (no cross-composite import).
 */
export const PunchupAutoRefreshToggle: FC<PunchupAutoRefreshToggleProps> = ({
	checked,
	onChange,
	id = 'punchup-auto-refresh',
}) => {
	const { t } = useTranslation();
	const label = t('auto-refresh', 'Auto-refresh');

	return (
		<div className={styles.item}>
			<label htmlFor={id} className={styles.label}>
				{label}
			</label>
			<PunchupToggle id={id} name={id} isChecked={checked} handleClick={() => onChange(!checked)} />
			<PunchupTooltip
				mainText="Refreshes every minute so it stays current while you watch the door — the same as clicking Refresh on a timer."
				secondaryText="Off by default — updates aren't pushed live, so without it you're seeing a snapshot from when you opened the page or last refreshed."
			>
				<span className={styles.help} tabIndex={0} aria-label={`${label} — help`}>
					<HelpCircle size={14} aria-hidden />
				</span>
			</PunchupTooltip>
		</div>
	);
};
